import React from 'react';
import { useQuery } from '@apollo/react-hooks';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';

import { Cohorts, COHORT_KEY } from '@confluence/onboarding-helpers/entry-points/constants/cohorts';
import { EditorPresenceAvatarGroupLoader } from '@confluence/editor-presence-avatar-group';
import { useSessionData, AccessStatus } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	ErrorDisplay,
	Attribution,
	ErrorBoundary,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

import {
	TEAMMATE_PRESENCE_FF,
	TEAMMATE_PRESENCE_FF_AA,
	TEAMMATE_PRESENCE_GATE,
} from './teammatePresenceConstants';
import { TeammatePresenceAvatarGroup } from './TeammatePresenceAvatarGroup';
import { TeammatePresenceLicenseConsumingUserCountQuery } from './TeammatePresenceLicenseConsumingUserCountQuery.graphql';
import { TeammatePresenceContainer } from './useTeammatePresenceStore';
import { TeammatePresencePlaceholder } from './TeammatePresencePlaceholder';
import type { TeammatePresenceLicenseConsumingUserCountQuery as TeammatePresenceLicenseConsumingUserCountQueryType } from './__types__/TeammatePresenceLicenseConsumingUserCountQuery';
import { NCSProvider } from './NCSProvider';

const MAX_USER_COUNT = 500;
const MIN_USER_COUNT = 2;

type TeammatePresenceProps = {
	collabEditOption?: CollabEditOptions;
	contentId: string;
	contentType: string | undefined | null;
	spaceKey: string;
	isEditorActionBarSSREnabled?: boolean;
};

const getIsInstanceWithinLimit = (userCount: number) => {
	return userCount >= MIN_USER_COUNT && userCount < MAX_USER_COUNT;
};

const TeammatePresenceComponent = ({
	contentId,
	contentType,
	spaceKey,
	collabEditOption,
	isEditorActionBarSSREnabled,
}: TeammatePresenceProps) => {
	const { edition, accessStatus } = useSessionData();
	const isLivePage = useIsLivePage();
	const isEditPage = useIsEditorPage();

	const isCorrectEdition =
		edition === ConfluenceEdition.FREE || edition === ConfluenceEdition.STANDARD;

	const isCorrectTypeOfUser =
		accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS ||
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const passesFeatureGate = FeatureGates.checkGate(TEAMMATE_PRESENCE_GATE);

	const { data, loading, error } = useQuery<TeammatePresenceLicenseConsumingUserCountQueryType>(
		TeammatePresenceLicenseConsumingUserCountQuery,
		{
			skip: !isCorrectEdition || !passesFeatureGate || !isCorrectTypeOfUser || isLivePage,
		},
	);
	if (isEditorActionBarSSREnabled && process.env.REACT_SSR) {
		return <TeammatePresencePlaceholder />;
	}

	if (loading) {
		if (isEditorActionBarSSREnabled) {
			return <TeammatePresencePlaceholder />;
		} else {
			return null;
		}
	}

	// This check inherently includes the right edition + type of user + checks feature gate
	if (data) {
		const isInstanceWithinUserLimit = getIsInstanceWithinLimit(
			data?.license?.licenseConsumingUserCount ?? 0,
		);

		isInstanceWithinUserLimit &&
			FeatureGates.getExperimentValue<Cohorts>(
				TEAMMATE_PRESENCE_FF_AA,
				COHORT_KEY,
				Cohorts.NOT_ENROLLED,
				{
					fireExperimentExposure: true,
				},
			);

		const isEligibleForTeammatePresence =
			isInstanceWithinUserLimit &&
			FeatureGates.getExperimentValue<Cohorts>(
				TEAMMATE_PRESENCE_FF,
				COHORT_KEY,
				Cohorts.NOT_ENROLLED,
				{
					fireExperimentExposure: true,
				},
			) === Cohorts.EXPERIMENT;

		if (isEligibleForTeammatePresence) {
			return (
				<TeammatePresenceContainer
					key={`${contentId}-${contentType}-${spaceKey}`}
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
				>
					<NCSProvider contentId={contentId}>
						<TeammatePresenceAvatarGroup collabEditOption={collabEditOption} />
					</NCSProvider>
				</TeammatePresenceContainer>
			);
		}
	}

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	// I surrounded only our query with the error display because I didn't want to consume the errors from `EditorPresenceAvatarGroupLoader`.
	return (
		<>
			{collabEditOption && (isEditPage || isLivePage) ? (
				<EditorPresenceAvatarGroupLoader
					collabEditOption={collabEditOption}
					isEditorActionBarSSREnabled={isEditorActionBarSSREnabled}
				/>
			) : null}
			{error && !isUnauthorizedError(error) && <ErrorDisplay error={error} />}
		</>
	);
};

export const TeammatePresence = (props: TeammatePresenceProps) => {
	return (
		<ErrorBoundary attribution={Attribution.CC_ONBOARDING}>
			<TeammatePresenceComponent {...props} />
		</ErrorBoundary>
	);
};
